import {
  EndCustomer,
  InvoicePdfTemplate,
  useGetInvoicePdfTemplatesPaginatedLazyQuery,
} from "@api/graphql/generated/generated-types";
import React, { useEffect } from "react";
import EditCustomerForm from "@src/endcustomers/components/EditCustomerForm";
import { Loading } from "@src/common/components";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { LabelManagement } from "../../LabelManagement/LabelManagement";
import { LabelEntityType } from "@api/graphql/generated/generated-types";
import { CustomerAccountComments } from "@src/endcustomers/components/CustomerAccountComments";

export interface CustomerWidgetState {
  data: {
    endCustomer: EndCustomer | null | undefined;
  };
  loading: boolean;
  error: any;
}

type Props = {
  endCustomer: EndCustomer | null | undefined;
  updateSelectedEndCustomerWithRes?: (res: any) => void;
  onCommentAdded?: () => void;
};

function CustomerWidget({
  endCustomer,
  updateSelectedEndCustomerWithRes,
  onCommentAdded,
}: Props) {
  const showError = useErrorToast();

  const [
    getInvoicePdfTemplates,
    {
      data: invoicePdfTemplatesData,
      loading: invoicePdfTemplatesLoading,
      error: invoicePdfTemplatesError,
    },
  ] = useGetInvoicePdfTemplatesPaginatedLazyQuery();

  useEffect(() => {
    if (endCustomer) {
      getInvoicePdfTemplates({
        variables: {
          courierId: endCustomer.courierId,
          limit: 100,
          after: 0,
        },
        fetchPolicy: "network-only",
      }).catch(showError);
    }
  }, [endCustomer]);

  if (endCustomer && invoicePdfTemplatesData) {
    const invoicePdfTemplates =
      invoicePdfTemplatesData?.invoicePdfTemplatesPaginated?.edges
        ?.map((edge) => edge?.node)
        .filter(
          (node): node is InvoicePdfTemplate =>
            node !== null && node !== undefined
        ) || [];

    return (
      <Loading loading={invoicePdfTemplatesLoading || false}>
        <div className="flex flex-col gap-3">
          <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-2xl">
            <LabelManagement
              entityId={endCustomer.id}
              entityLabels={endCustomer.assignedLabels}
              onLabelAssignmentChanged={
                updateSelectedEndCustomerWithRes || (() => {})
              }
              entityType={LabelEntityType.EndCustomer}
            />
            <div className="p-3 pt-0">
              <span className="font-semibold mb-2 dark:text-gray-100">Customer</span>
              <div className="rounded-lg border dark:border-gray-700 shadow-md dark:bg-gray-700 p-2 pt-2">
                <EditCustomerForm
                  endCustomer={endCustomer}
                  invoicePdfTemplates={invoicePdfTemplates}
                  maxWidth={"100%"}
                  showNote={true}
                  onSuccess={(res) => {
                    updateSelectedEndCustomerWithRes &&
                    updateSelectedEndCustomerWithRes(res);
                  }}
                />
              </div>
            </div>
          </div>
          {onCommentAdded && !invoicePdfTemplatesLoading && (
            <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-2xl p-3">
              <span className="font-semibold block mb-3 dark:text-gray-100">Internal Account Comments</span>
              <CustomerAccountComments
                endCustomer={endCustomer}
                onCommentAdded={onCommentAdded}
              />
            </div>
          )}
        </div>
      </Loading>
    );
  }
  return null;
}

export default CustomerWidget;
